import React, { useEffect, useState } from "react";
import { Form, Input, Select, Button, Alert, Space } from "antd";
import {
  listDemoSchools,
  provisionDemoUser,
} from "../../../services/demo/demo.service";
import type { DemoSchoolListItem } from "../../../services/demo/demo.types";

export interface AddDemoUserViewProps {
  onProvisioned: (credentials: {
    email: string;
    password: string;
    schoolName: string;
    classroomsCreated: number;
  }) => void;
  refreshToken: number;
}

const AddDemoUserView: React.FC<AddDemoUserViewProps> = ({
  onProvisioned,
  refreshToken,
}) => {
  const [form] = Form.useForm();
  const [schools, setSchools] = useState<DemoSchoolListItem[]>([]);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedSchoolId, setSelectedSchoolId] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<
    { type: "success" | "error"; text: string } | null
  >(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingSchools(true);
    listDemoSchools()
      .then((data) => {
        if (!cancelled) setSchools(data);
      })
      .catch(() => {
        if (!cancelled) {
          setFeedback({
            type: "error",
            text: "No se pudieron cargar las escuelas demo. Recargá la página.",
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingSchools(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshToken]);

  const selectedSchool = schools.find((s) => s.id === selectedSchoolId) ?? null;
  const availableGrades = selectedSchool?.grades ?? [];

  const handleSubmit = async (values: {
    schoolId: number;
    firstName: string;
    lastName: string;
    email: string;
    grades: string[];
  }) => {
    setSubmitting(true);
    setFeedback(null);
    try {
      const result = await provisionDemoUser({
        schoolId: Number(values.schoolId),
        teacher: {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
        },
        grades: values.grades,
      });
      onProvisioned({
        email: result.email,
        password: result.password,
        schoolName: result.schoolName,
        classroomsCreated: result.classroomsCreated,
      });
      form.resetFields();
      setSelectedSchoolId(null);
    } catch (error: any) {
      const status = error?.response?.status;
      if (!error?.response) {
        setFeedback({ type: "error", text: "Sin conexión. Reintentá." });
      } else if (status === 409) {
        form.setFields([
          {
            name: "email",
            errors: [
              "Este correo ya está en uso. Usá un email diferente para el docente.",
            ],
          },
        ]);
        form.scrollToField("email");
      } else if (status === 400 || status === 404) {
        const serverMsg =
          error?.response?.data?.message ??
          "Datos inválidos. Verificá el formulario.";
        setFeedback({ type: "error", text: serverMsg });
      } else {
        setFeedback({
          type: "error",
          text: "Error al provisionar el usuario demo. Intentá de nuevo.",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      {feedback && (
        <Alert
          type={feedback.type}
          message={feedback.text}
          closable
          afterClose={() => setFeedback(null)}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
        onValuesChange={(changedValues) => {
          if ("schoolId" in changedValues) {
            const raw = changedValues.schoolId;
            // AntD Select returns numeric option values as numbers in
            // production, but the native <select> used in tests passes strings.
            const newSchoolId =
              raw === null || raw === undefined || raw === ""
                ? null
                : Number(raw);
            setSelectedSchoolId(newSchoolId);
            // Clear grades when school changes — selected grades may not exist
            // in the newly chosen school.
            form.setFieldsValue({ grades: [] });
          }
        }}
      >
        <Form.Item
          label="Escuela demo"
          name="schoolId"
          rules={[{ required: true, message: "Seleccioná una escuela demo" }]}
          extra="Solo se muestran escuelas demo. Si no ves la escuela, creala primero en la pestaña 'Crear escuela demo'."
        >
          <Select
            placeholder="Seleccioná una escuela"
            loading={loadingSchools}
            disabled={loadingSchools}
            optionFilterProp="label"
            showSearch
            options={schools.map((s) => ({
              value: s.id,
              label: s.name,
              title: s.name,
            }))}
          />
        </Form.Item>

        <Form.Item
          label="Nombre del cliente"
          name="firstName"
          rules={[{ required: true, message: "Ingresá el nombre" }]}
        >
          <Input placeholder="Ej: María" />
        </Form.Item>

        <Form.Item
          label="Apellido del cliente"
          name="lastName"
          rules={[{ required: true, message: "Ingresá el apellido" }]}
        >
          <Input placeholder="Ej: González" />
        </Form.Item>

        <Form.Item
          label="Email del cliente"
          name="email"
          rules={[
            { required: true, message: "Ingresá el email" },
            { type: "email", message: "El formato de email no es válido" },
          ]}
        >
          <Input placeholder="Ej: maria.gonzalez@cliente.mx" />
        </Form.Item>

        <Form.Item
          label="Grados a mostrar al usuario demo"
          name="grades"
          rules={[
            { required: true, message: "Seleccioná al menos un grado" },
          ]}
          extra={
            selectedSchool
              ? "Se reutilizan los classrooms ya existentes de la escuela."
              : "Seleccioná una escuela primero para ver sus grados."
          }
        >
          <Select
            mode="multiple"
            placeholder="Seleccioná uno o más grados"
            disabled={!selectedSchool}
            optionFilterProp="label"
            options={availableGrades.map((g) => ({
              value: g,
              label: g,
              title: g,
            }))}
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={submitting}
            disabled={submitting}
          >
            Agregar usuario demo
          </Button>
        </Form.Item>
      </Form>
    </Space>
  );
};

export default AddDemoUserView;
